import { BubbleMenu, Editor } from "@tiptap/react";
import { FC, useCallback, useState, useEffect } from "react";
import { Button } from "@src/lib/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { Textarea } from "@src/lib/ui/Textarea";
import Text from "@src/lib/ui/Text";
import PhotoSparkleOutlineIcon from "@src/lib/icons/18px/photo-sparkle-outline";
import PhotoOutlineIcon from "@src/lib/icons/18px/photo-outline";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import CropIcon from "@src/lib/icons/18px/crop";
import CircleQuestionOutlineIcon from "@src/lib/icons/18px/circle-question-outline";
import SparklesIcon from "@src/lib/icons/18px/sparkles-2-fill";
import { useModifyImageMutation } from "@src/features/aiAssist/dialogs/AIImageEditDialog.generated";
import { useToast } from "@src/lib/hooks/useToast";
import { cn } from "@src/lib/utils/cn";
import { useTracking } from "@src/features/app/hooks/useTracking";
import Spinner from "@src/lib/ui/Spinner";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";
import { useS3Upload } from "@src/features/app/hooks/useS3Upload";
import PinturaModal from "@src/lib/ui/PinturaModal";
import AddMediaDialog from "@src/features/app/dialogs/addMedia/AddMediaDialog";
import { getMediaUrl } from "@src/features/contentDetail/utils/media";
import { LinkType } from "@src/lib/utils/files";
import { AlignCenter, AlignLeft, AlignRight } from "lucide-react";
import {
  applyImageAlignToStyle,
  getImageAlignFromStyle,
  ImageAlign,
} from "./imageAlign";
import { hideVendorImageAlignBars } from "./vendorImageControls";

type Props = {
  editor: Editor;
};

export const TrainingResourceImageBubbleMenu: FC<Props> = ({ editor }) => {
  const isAiImageGenEnabled = useFeatureFlag(Ff.AiImageGen);
  const [showAIEditor, setShowAIEditor] = useState(false);
  const [showPinturaModal, setShowPinturaModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [isAddMediaOpen, setIsAddMediaOpen] = useState(false);
  const [selectedImagePos, setSelectedImagePos] = useState<number | null>(null);
  const [modifyImage, { loading: isProcessing }] = useModifyImageMutation();
  const { addErrorToast, addToast } = useToast();
  const { trackEvent } = useTracking();

  const { handleFileUpload, isUploading } = useS3Upload({
    onUploadComplete: (upload) => {
      editor
        .chain()
        .focus()
        .updateAttributes("image", { src: upload.url })
        .run();
      addToast({
        message: "Image replaced successfully!",
      });
    },
  });

  // Add click event listeners to images to select them (instead of hover)
  // Using click instead of hover prevents stealing the text cursor when users
  // are typing near images
  useEffect(() => {
    const handleImageClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "IMG" && target.closest(".ProseMirror")) {
        // Find the image node position in the document
        const pos = editor.view.posAtDOM(target, 0);
        if (pos !== null && pos !== undefined) {
          // Select the image node
          const node = editor.state.doc.nodeAt(pos);
          if (node && node.type.name === "image") {
            editor.commands.setNodeSelection(pos);
          }
        }
        // tiptap-extension-resize-image paints its own align bar on the
        // same click; it covers this bubble menu on large images. Keep the
        // element in place because the extension removes five children by
        // position on the next click (align bar + four resize handles).
        hideVendorImageAlignBars(editor.view.dom);
        requestAnimationFrame(() => hideVendorImageAlignBars(editor.view.dom));
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener("click", handleImageClick);

    return () => {
      editorElement.removeEventListener("click", handleImageClick);
    };
  }, [editor]);

  const getCurrentImageSrc = useCallback(() => {
    const { selection, doc } = editor.state;
    const { from } = selection;

    // Try to get the node at the current selection
    const node = doc.nodeAt(from);
    if (node && node.type.name === "image") {
      return node.attrs.src;
    }

    // If selection is inside an image node, check the parent
    const $pos = doc.resolve(from);
    if ($pos.parent.type.name === "image") {
      return $pos.parent.attrs.src;
    }

    return null;
  }, [editor]);

  const isCurrentImageGif = useCallback(() => {
    const src = getCurrentImageSrc();
    if (!src) return false;
    return src.toLowerCase().endsWith(".gif");
  }, [getCurrentImageSrc]);

  const handleDelete = useCallback(() => {
    editor.chain().focus().deleteSelection().run();
  }, [editor]);

  const getCurrentImageStyle = useCallback(() => {
    const { selection, doc } = editor.state;
    const node = doc.nodeAt(selection.from);
    if (node?.type.name === "image") {
      return node.attrs.style as string | null;
    }
    return null;
  }, [editor]);

  const handleAlign = useCallback(
    (align: ImageAlign) => {
      const style = applyImageAlignToStyle(getCurrentImageStyle(), align);
      editor.chain().focus().updateAttributes("image", { style }).run();
    },
    [editor, getCurrentImageStyle],
  );

  const currentAlign = getImageAlignFromStyle(getCurrentImageStyle());

  // No-op: replace now handled via AddMediaDialog

  const handleEditWithAI = useCallback(async () => {
    if (!aiPrompt.trim()) {
      addErrorToast({
        message: "Please enter a prompt describing how to edit the image",
        callsite: "ai_image_edit_doc_builder",
      });
      return;
    }

    const currentSrc = getCurrentImageSrc();
    if (!currentSrc) {
      addErrorToast({
        message: "Could not find image to edit",
        callsite: "ai_image_edit_doc_builder",
      });
      return;
    }

    const startTime = Date.now();

    try {
      const result = await modifyImage({
        variables: {
          input: {
            imageUrl: currentSrc,
            prompt: aiPrompt.trim(),
          },
        },
      });

      if (
        result.data?.modifyImage?.success &&
        result.data?.modifyImage?.imageUrl
      ) {
        const newUrl = result.data.modifyImage.imageUrl;
        const duration = Date.now() - startTime;

        // Update the image src in the editor
        editor.chain().focus().updateAttributes("image", { src: newUrl }).run();

        addToast({
          message: "Image edited successfully with AI!",
        });
        setShowAIEditor(false);
        setAiPrompt("");

        // Track successful AI image modification
        trackEvent("ai_image_modified", {
          prompt: aiPrompt.trim(),
          original_url: currentSrc,
          modified_url: newUrl,
          duration_ms: duration,
          location: "doc_builder_bubble_menu",
        });
      } else {
        addErrorToast({
          message:
            result.data?.modifyImage?.error?.code === "AI_SERVICE_UNAVAILABLE"
              ? "The AI image service is temporarily unavailable. Please try again."
              : "Failed to edit image with AI. Please try again.",
          callsite: "ai_image_edit_doc_builder",
        });
      }
    } catch {
      addErrorToast({
        message: "Something went wrong. Please try again.",
        callsite: "ai_image_edit_doc_builder",
      });
    }
  }, [
    aiPrompt,
    getCurrentImageSrc,
    modifyImage,
    addToast,
    addErrorToast,
    editor,
    trackEvent,
  ]);

  return (
    <>
      <BubbleMenu
        tippyOptions={{
          duration: 100,
          interactive: true,
          placement: "bottom",
          offset: [0, -20],
          // Ensure modal and overlays appear above the bubble menu
          zIndex: 100,
          onHide: () => {
            if (!isUploading && !showPinturaModal && !isAddMediaOpen) {
              setShowAIEditor(false);
              setAiPrompt("");
            }
          },
        }}
        editor={editor}
        className={cn(
          "flex h-10 items-center gap-0 rounded-full bg-bg-elevated p-1 shadow-out-lg",
          isAddMediaOpen && "hidden",
        )}
        shouldShow={({ state, from }) => {
          // Hide when AddMediaDialog is open
          if (isAddMediaOpen) return false;
          const { doc } = state;
          const node = doc.nodeAt(from);
          return node?.type.name === "image";
        }}
      >
        <TooltipProvider>
          {isAiImageGenEnabled && !isCurrentImageGif() && (
            <Popover open={showAIEditor} onOpenChange={setShowAIEditor}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="md">
                  <PhotoSparkleOutlineIcon className="size-4" />
                  <span className="ml-1.5 text-xs font-medium">
                    Edit with AI
                  </span>
                </Button>
              </PopoverTrigger>

              <PopoverContent
                side="bottom"
                align="start"
                className="w-96 rounded-xl border border-gray-2 p-0 shadow-lg"
                onOpenAutoFocus={(e) => e.preventDefault()}
                disablePortal
              >
                <div className="flex flex-col overflow-hidden rounded-xl bg-white">
                  <div className="flex items-center justify-between border-b border-gray-3 px-3 py-2">
                    <Text type="P2" fontWeight="SemiBold">
                      Describe what you'd like to change
                    </Text>
                    <a
                      href="https://help.opus.so/en/articles/12624830-best-practices-for-ai-image-generation"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-gray-6 hover:text-gray-11"
                    >
                      <CircleQuestionOutlineIcon className="size-3.5 text-gray-6" />
                      <span className="text-xs text-gray-6">Prompt help</span>
                    </a>
                  </div>
                  <Textarea
                    placeholder="E.g., brighten image, blur background, add warm lighting"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (aiPrompt.trim() && !isProcessing) {
                          handleEditWithAI();
                        }
                      }
                    }}
                    rows={4}
                    className="resize-none border-none p-3 focus-visible:ring-0 focus-visible:ring-offset-0"
                    disabled={isProcessing}
                  />
                  <div className="flex items-center justify-end p-2">
                    <Button
                      size="sm"
                      className="gap-1.5 bg-ai px-3 text-sm text-white hover:bg-purple-9"
                      onClick={handleEditWithAI}
                      disabled={!aiPrompt.trim() || isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <Spinner />
                          <span className="ml-2">Editing...</span>
                        </>
                      ) : (
                        <>
                          <SparklesIcon />
                          Generate
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}

          <Tooltip>
            <AddMediaDialog
              title="Replace image"
              onOpenChange={(open) => {
                setIsAddMediaOpen(open);
                if (open) {
                  const { from } = editor.state.selection;
                  const node = editor.state.doc.nodeAt(from);
                  if (node && node.type.name === "image") {
                    setSelectedImagePos(from);
                  } else {
                    setSelectedImagePos(null);
                  }
                } else {
                  setSelectedImagePos(null);
                }
              }}
              filterResources={[LinkType.IMAGE, LinkType.GIF]}
              onMediaChange={(media) => {
                const mediaUrl = getMediaUrl(media);
                if (mediaUrl) {
                  let chain = editor.chain().focus();
                  if (selectedImagePos !== null) {
                    chain = chain.setNodeSelection(selectedImagePos);
                  }
                  chain.updateAttributes("image", { src: mediaUrl }).run();
                  addToast({ message: "Image replaced successfully!" });
                }
              }}
              onResourceChange={(trainingResource) => {
                const mediaUrl = trainingResource.publishedVersion?.media
                  ? getMediaUrl(trainingResource.publishedVersion.media)
                  : null;
                if (mediaUrl) {
                  let chain = editor.chain().focus();
                  if (selectedImagePos !== null) {
                    chain = chain.setNodeSelection(selectedImagePos);
                  }
                  chain.updateAttributes("image", { src: mediaUrl }).run();
                  addToast({ message: "Image replaced successfully!" });
                }
              }}
              trigger={
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-md" disabled={isUploading}>
                    <PhotoOutlineIcon className="size-4" />
                  </Button>
                </TooltipTrigger>
              }
            />
            <TooltipContent side="bottom">Replace image</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-md"
                onClick={() => setShowPinturaModal(true)}
              >
                <CropIcon className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Crop image</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-md" onClick={handleDelete}>
                <TrashOutlineIcon className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Delete image</TooltipContent>
          </Tooltip>

          <div className="mx-1 h-4 w-px bg-gray-4" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-md"
                className={cn(currentAlign === "left" && "bg-state-pressed")}
                onClick={() => handleAlign("left")}
              >
                <AlignLeft className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Align left</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-md"
                className={cn(currentAlign === "center" && "bg-state-pressed")}
                onClick={() => handleAlign("center")}
              >
                <AlignCenter className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Align center</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-md"
                className={cn(currentAlign === "right" && "bg-state-pressed")}
                onClick={() => handleAlign("right")}
              >
                <AlignRight className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Align right</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </BubbleMenu>

      {getCurrentImageSrc() && showPinturaModal && (
        <PinturaModal
          onNewImageFile={(imageFile) => {
            handleFileUpload(imageFile);
            setShowPinturaModal(false);
          }}
          imageUrl={getCurrentImageSrc() || ""}
          visible={showPinturaModal}
          hide={() => setShowPinturaModal(false)}
        />
      )}
    </>
  );
};
